/**
 * The row-affordance rule: a control that stays out of the way until its row
 * is hovered or contains focus.
 *
 * Opacity alone hides nothing from *input*. A control at `opacity-0` is still
 * clickable and still takes focus, so it was permanently invisible yet
 * tappable, and a keyboard user could tab straight into a button they could
 * not see. Pairing the opacity with `pointer-events` fixes the first; the
 * `group-focus-within:` half fixes the second.
 *
 * The `(hover: none)` escape is not optional, and is the part that is easy to
 * get wrong. Tailwind v4 compiles every `hover:`/`group-hover:` rule inside
 * `@media (hover: hover)`, so on a touch device those rules never apply at
 * all — leaving only `opacity-0 pointer-events-none`, i.e. a control that is
 * both invisible and inert, with no gesture that can reveal it. (Tapping the
 * row does not help: the reveal is gated by the media query, not by the tap.)
 * On a device that cannot hover, these controls are therefore simply always
 * visible and always live. Verified against the pinned Tailwind by compiling
 * the variant, not assumed.
 *
 * One decision, one place: this was spelled out as a seven-class literal at
 * two dozen call sites, already drifting between `transition-all` and
 * `transition-opacity` and between including and omitting the pointer-events
 * half. Adding the coarse-pointer path above was a one-line edit here rather
 * than a twenty-five-line sweep, which is the whole point.
 *
 * Requires an ancestor carrying Tailwind's `group` class.
 *
 * Requires, too, that the element is *unconditionally rendered*. Every rule
 * here is a rule about an element that exists — pair one of these constants
 * with a JS gate that unmounts the control (a `!isMobile` branch, a hover flag
 * driven by `mouseenter`, a primitive that returns `null`) and the gate silently
 * voids all of it: the `(hover: none)` escape has nothing to make visible, the
 * `group-focus-within:` half has nothing to focus, and the row's height moves as
 * the element comes and goes. If visibility is worth deciding in JS, that JS —
 * not this class — owns the whole decision, including the touch and keyboard
 * cases. LG-333 is the instance: an action bar unmounted by `autohide="not-last"`
 * kept this class list and was still unreachable on touch.
 */

/** Interactive controls — buttons, links, drag handles. */
export const REVEAL_ON_HOVER =
  'opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto'

/**
 * Non-interactive indicators — a chevron, a divider. They take no clicks, so
 * they need no pointer-events juggling, but they still reveal on focus so the
 * affordance reads the same to a keyboard user as to a mouse, and on a
 * touch device where the hover reveal can never fire.
 */
export const REVEAL_ON_HOVER_DECORATIVE =
  'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100'

/**
 * Full-bleed hover scrims — an overlay that covers its card to label what a
 * click would do ("Apply"). This is the one case where the two rules above are
 * both wrong, so it gets its own name rather than a hand-written literal:
 *
 * - **No `[@media(hover:none)]` escape.** That escape exists so a control with
 *   no reveal gesture is still reachable. A scrim is not a control — the card
 *   underneath is tappable either way — so revealing it on touch buys nothing
 *   and costs everything: `inset-0 bg-black/40` over every card, permanently,
 *   hiding the very thumbnail the user is choosing between.
 * - **No `group-focus-within:`.** A scrim covers its whole card, including any
 *   input that card puts in flow (the inline rename field). Revealing on focus
 *   paints the scrim over the field the moment it autofocuses.
 * - **`pointer-events-none` unconditionally.** The scrim is painted above
 *   everything in the card because it is positioned; without this it swallows
 *   clicks aimed at what it covers even while fully transparent.
 */
export const REVEAL_ON_HOVER_SCRIM =
  'opacity-0 pointer-events-none transition-opacity group-hover:opacity-100'
